import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../../../firebase/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../../../components/Sidebar";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import { useAnalytics } from "../../../firebase/hooks/useAnalytics";
import getApiUrl from "../../../config/api";
import axios from "axios";

export default function Feedback() {
  const { currentUser, getUserProfile, isAuthReady } = useAuth();
  const [showSuccess, setShowSuccess] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { trackPageView } = useAnalytics();

  const profile = getUserProfile();

  const getLangFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get("lang") === "zh" ? "zh" : "en";
  };

  const [language, setLanguage] = useState(getLangFromUrl());
  const [feedbackData, setFeedbackData] = useState({
    category: "",
    subject: "",
    message: "",
    email: profile?.email || "",
    name: profile?.displayName || "",
    screenshots: [],
  });
  const fileInputRef = useRef(null);
  const screenshotsRef = useRef([]); // Track screenshots for cleanup

  useEffect(() => {
    trackPageView("Feedback Page");
  }, [trackPageView]);

  // Keep language in sync with URL (if user pastes a new URL)
  useEffect(() => {
    setLanguage(getLangFromUrl());
    // eslint-disable-next-line
  }, [location.search]);

  useEffect(() => {
    if (!currentUser) {
      navigate({ pathname: "/login", search: `?lang=${language}` });
    }
  }, [currentUser, navigate, language]);

  // Update URL when language changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("lang") !== language) {
      params.set("lang", language);
      navigate({ search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line
  }, [language]);

  // Update form data when profile changes
  useEffect(() => {
    if (profile) {
      setFeedbackData((prev) => ({
        ...prev,
        email: profile.email || "",
        name: profile.displayName || "",
      }));
    }
    // Only depend on the specific values we need: `profile` itself is a fresh
    // object on every render and would re-run this on each one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.email, profile?.displayName]);

  // Cleanup screenshot URLs on unmount only
  useEffect(() => {
    return () => {
      // Cleanup all screenshots tracked in ref
      screenshotsRef.current.forEach((screenshot) => {
        URL.revokeObjectURL(screenshot.preview);
      });
    };
  }, []); // Empty dependency array - only run on unmount

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFeedbackData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedbackLoading(true);
    try {
      // Check if currentUser and getIdToken exist
      if (!currentUser || typeof currentUser.getIdToken !== "function") {
        // Try to refresh the user token
        try {
          // Firebase Auth reload
          if (currentUser && typeof currentUser.reload === "function") {
            await currentUser.reload();
            // After reload, check again
            if (typeof currentUser.getIdToken === "function") {
              // Token should be refreshed, continue
            } else {
              throw new Error("getIdToken still not available after reload");
            }
          } else {
            throw new Error("reload not available on currentUser");
          }
        } catch (refreshError) {
          setErrorMessage(
            language === "en"
              ? "Session expired. Please log in again to submit feedback."
              : "会话已过期。请重新登录后提交反馈。",
          );
          setShowError(true);
          setTimeout(() => {
            setShowError(false);
          }, 3000);
          setFeedbackLoading(false);
          return;
        }
      }

      // Create FormData to handle both text and file data
      const formData = new FormData();
      formData.append("name", profile.name);
      formData.append("email", profile.email);
      formData.append("category", feedbackData.category);
      formData.append("subject", feedbackData.subject);
      formData.append("message", feedbackData.message);
      feedbackData.screenshots.forEach((screenshot, index) => {
        formData.append(`screenshots`, screenshot.file);
      });

      const token = await currentUser.getIdToken();
      axios
        .post(`${getApiUrl()}/send-feedback`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          setShowSuccess(true);
          setTimeout(() => {
            setShowSuccess(false);
          }, 3000);
          setFeedbackData({
            category: "",
            subject: "",
            message: "",
            email: profile?.email || "",
            name: profile?.displayName || "",
            screenshots: [],
          });
        })
        .catch((error) => {
          if (error.response) {
            const statusCode = error.response.status;
            const retryAfter = error.response.data?.retry_after || null;
            let personalizedMessage = "";
            if (statusCode === 400) {
              personalizedMessage =
                language === "en"
                  ? "Invalid form data. Please check your inputs and try again."
                  : "表单数据无效。请检查您的输入并重试。";
            } else if (statusCode === 401) {
              personalizedMessage =
                language === "en"
                  ? "Authentication failed. Please log in again."
                  : "身份验证失败。请重新登录。";
            } else if (statusCode === 403) {
              personalizedMessage =
                language === "en"
                  ? "Access denied. You don't have permission to submit feedback."
                  : "访问被拒绝。您没有提交反馈的权限。";
            } else if (statusCode === 413) {
              personalizedMessage =
                language === "en"
                  ? "Files too large. Please reduce image sizes and try again."
                  : "文件过大。请减小图片大小后重试。";
            } else if (statusCode === 422) {
              personalizedMessage =
                language === "en"
                  ? "Invalid email format or missing required fields."
                  : "邮箱格式无效或缺少必填字段。";
            } else if (statusCode === 429) {
              personalizedMessage =
                language === "en"
                  ? `Too many requests. Please wait ${
                      retryAfter || 60
                    } seconds before trying again.`
                  : `请求过于频繁。请等待 ${retryAfter || 60} 秒后重试。`;
            } else if (statusCode >= 500) {
              personalizedMessage =
                language === "en"
                  ? "Server error. Our team has been notified. Please try again later."
                  : "服务器错误。我们的团队已收到通知。请稍后重试。";
            } else {
              personalizedMessage =
                language === "en"
                  ? `Service temporarily unavailable (Error ${statusCode}). Please try again later.`
                  : `服务暂时不可用（错误 ${statusCode}）。请稍后重试。`;
            }
            setErrorMessage(personalizedMessage);
            setShowError(true);
            setTimeout(() => {
              setShowError(false);
            }, 3000);
          } else {
            const networkMessage =
              language === "en"
                ? "Network connection failed. Please check your internet connection and try again."
                : "网络连接失败。请检查您的网络连接并重试。";
            setErrorMessage(networkMessage);
            setShowError(true);
            setTimeout(() => {
              setShowError(false);
            }, 3000);
          }
        })
        .finally(() => {
          setFeedbackLoading(false);
        });
    } catch (error) {
      console.error("Error submitting feedback:", error);
      const generalErrorMessage =
        language === "en"
          ? "Unexpected error occurred. Please try again or contact support."
          : "发生意外错误。请重试或联系支持。";
      setErrorMessage(generalErrorMessage);
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 3000);
      setFeedbackLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const maxFiles = 3;
    const maxSize = 5 * 1024 * 1024; // 5MB per file

    if (feedbackData.screenshots.length + files.length > maxFiles) {
      alert(`Maximum ${maxFiles} screenshots allowed`);
      return;
    }

    const validFiles = files.filter((file) => {
      if (file.size > maxSize) {
        alert(`File ${file.name} is too large. Maximum size is 5MB.`);
        return false;
      }
      if (!file.type.startsWith("image/")) {
        alert(`File ${file.name} is not an image.`);
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      const newScreenshots = validFiles.map((file, index) => ({
        id: Date.now() + index,
        file: file,
        preview: URL.createObjectURL(file),
        name: file.name,
      }));

      // Update ref for cleanup
      screenshotsRef.current = [...screenshotsRef.current, ...newScreenshots];

      setFeedbackData((prev) => ({
        ...prev,
        screenshots: [...prev.screenshots, ...newScreenshots],
      }));
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeScreenshot = (id) => {
    setFeedbackData((prev) => {
      const updatedScreenshots = prev.screenshots.filter(
        (img) => img.id !== id,
      );
      // Cleanup URL object to prevent memory leaks
      const removedImage = prev.screenshots.find((img) => img.id === id);
      if (removedImage) {
        URL.revokeObjectURL(removedImage.preview);
        // Update ref to remove the screenshot
        screenshotsRef.current = screenshotsRef.current.filter(
          (img) => img.id !== id,
        );
      }
      return {
        ...prev,
        screenshots: updatedScreenshots,
      };
    });
  };

  const categories =
    language === "en"
      ? [
          "Bug Report",
          "Feature Request",
          "General Feedback",
          "User Experience",
          "Performance",
          "Other",
        ]
      : ["錯誤回報", "功能請求", "一般反饋", "用戶體驗", "性能", "其他"];

  const texts =
    language === "en"
      ? {
          title: "Feedback",
          subtitle:
            "We value your feedback! Please share your thoughts with us.",
          category: "Category",
          selectCategory: "Select a category",
          subject: "Subject",
          subjectPlaceholder: "Brief description of your feedback",
          name: "Name",
          email: "Email",
          message: "Message",
          messagePlaceholder: "Please provide detailed feedback...",
          screenshots: "Screenshots (Optional)",
          screenshotsDesc:
            "Add up to 3 screenshots to help illustrate your feedback",
          addScreenshots: "Add Screenshots",
          submit: "Submit Feedback",
          cancel: "Cancel",
        }
      : {
          title: "反饋",
          subtitle: "我們重視您的反饋！請與我們分享您的想法。",
          category: "類別",
          selectCategory: "選擇類別",
          subject: "主題",
          subjectPlaceholder: "反饋的簡要描述",
          name: "姓名",
          email: "電子郵件",
          message: "訊息",
          messagePlaceholder: "請提供詳細的反饋...",
          screenshots: "螢幕截圖（可選）",
          screenshotsDesc: "添加最多 3 張螢幕截圖以幫助說明您的反饋",
          addScreenshots: "添加螢幕截圖",
          submit: "提交反饋",
          cancel: "取消",
        };

  if (!currentUser || !profile || !isAuthReady) {
    return (
      <div className="flex h-screen bg-slate-100 dark:bg-dashboard-bg text-black font-sans">
        {/* <Sidebar language={language} /> */}
        <LanguageSwitcher language={language} setLanguage={setLanguage} />
        <div className="flex-1 flex flex-col gap-8 items-center justify-center -mt-36">
          <div className="flex flex-col gap-4 items-center text-center">
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                <div className="w-3 h-3 bg-orange-500 rounded-full animate-bounce"></div>
                <div
                  className="w-3 h-3 bg-orange-500 rounded-full animate-bounce"
                  style={{ animationDelay: "0.1s" }}
                ></div>
                <div
                  className="w-3 h-3 bg-orange-500 rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                ></div>
              </div>
              <span className="text-orange-500 text-lg font-semibold">
                {language === "zh" ? "正在加载数据..." : "Loading data..."}
              </span>
            </div>
            <div className="text-sm text-orange-600 dark:text-orange-200">
              {language === "zh"
                ? "请稍候，正在为您准备反馈面板。"
                : "Please wait while we prepare your feedback panel."}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-100 dark:bg-dashboard-bg font-sans">
      <Sidebar />
      <LanguageSwitcher language={language} setLanguage={setLanguage} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto lg:p-16 md:p-8 p-4 pt-12">
          <div className="max-w-4xl mx-auto flex flex-col gap-1">
            {/* Header */}
            <div className="mt-12 mb-4 lg:my-8 flex flex-col items-center px-4">
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 dark:text-white mb-1 lg:mb-2">
                {texts.title}
              </h1>
              <p className="text-sm lg:text-base text-gray-600 dark:text-slate-100 text-center">
                {texts.subtitle}
              </p>
            </div>

            {/* Success Message (top of form) */}
            <div className="flex items-center justify-center px-2">
              {showSuccess && (
                <div className="bg-orange-400 text-white text-center px-3 md:px-4 py-4 md:py-6 rounded-md font-semibold animate-fadeInOut mt-4 w-full max-w-md">
                  <div className="flex items-center justify-center gap-2 md:gap-4 px-1 md:px-2">
                    <svg
                      className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      stroke="#ffffffff"
                    >
                      <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      ></g>
                      <g id="SVGRepo_iconCarrier">
                        <path
                          d="M4 12.6111L8.92308 17.5L20 6.5"
                          stroke="#ffffffff"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></path>
                      </g>
                    </svg>
                    <span className="text-sm md:text-base">
                      {language === "en"
                        ? "Submitted successfully!"
                        : "已成功提交！"}
                    </span>
                  </div>
                </div>
              )}
              {showError && (
                <div className="bg-red-600 text-white text-center px-3 md:px-4 py-4 md:py-6 rounded-md font-semibold animate-fadeInOut w-full max-w-md">
                  <div className="flex items-center justify-center gap-2 md:gap-4 px-1 md:px-2">
                    <svg
                      className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0"
                      viewBox="0 -0.5 25 25"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      stroke="#ffffff"
                    >
                      <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                      <g
                        id="SVGRepo_tracerCarrier"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></g>
                      <g id="SVGRepo_iconCarrier">
                        {" "}
                        <path
                          d="M6.96967 16.4697C6.67678 16.7626 6.67678 17.2374 6.96967 17.5303C7.26256 17.8232 7.73744 17.8232 8.03033 17.5303L6.96967 16.4697ZM13.0303 12.5303C13.3232 12.2374 13.3232 11.7626 13.0303 11.4697C12.7374 11.1768 12.2626 11.1768 11.9697 11.4697L13.0303 12.5303ZM11.9697 11.4697C11.6768 11.7626 11.6768 12.2374 11.9697 12.5303C12.2626 12.8232 12.7374 12.8232 13.0303 12.5303L11.9697 11.4697ZM18.0303 7.53033C18.3232 7.23744 18.3232 6.76256 18.0303 6.46967C17.7374 6.17678 17.2626 6.17678 16.9697 6.46967L18.0303 7.53033ZM13.0303 11.4697C12.7374 11.1768 12.2626 11.1768 11.9697 11.4697C11.6768 11.7626 11.6768 12.2374 11.9697 12.5303L13.0303 11.4697ZM16.9697 17.5303C17.2626 17.8232 17.7374 17.8232 18.0303 17.5303C18.3232 17.2374 18.3232 16.7626 18.0303 16.4697L16.9697 17.5303ZM11.9697 12.5303C12.2626 12.8232 12.7374 12.8232 13.0303 12.5303C13.3232 12.2374 13.3232 11.7626 13.0303 11.4697L11.9697 12.5303ZM8.03033 6.46967C7.73744 6.17678 7.26256 6.17678 6.96967 6.46967C6.67678 6.76256 6.67678 7.23744 6.96967 7.53033L8.03033 6.46967ZM8.03033 17.5303L13.0303 12.5303L11.9697 11.4697L6.96967 16.4697L8.03033 17.5303ZM13.0303 12.5303L18.0303 7.53033L16.9697 6.46967L11.9697 11.4697L13.0303 12.5303ZM11.9697 12.5303L16.9697 17.5303L18.0303 16.4697L13.0303 11.4697L11.9697 12.5303ZM13.0303 11.4697L8.03033 6.46967L6.96967 7.53033L11.9697 12.5303L13.0303 11.4697Z"
                          fill="#ffffff"
                        ></path>{" "}
                      </g>
                    </svg>
                    <div className="text-left text-sm md:text-base">
                      {errorMessage ||
                        (language === "en" ? (
                          <>
                            Error submitting feedback!
                            <br />
                            Check your email or try again later
                          </>
                        ) : (
                          <>
                            反馈提交错误！
                            <br />
                            请检查您的电子邮件或稍后再试
                          </>
                        ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Feedback Form */}
            <div className="bg-white dark:bg-slate-100 rounded-lg shadow-lg p-4 md:p-6 lg:p-8 relative mx-2 md:mx-0">
              {feedbackLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-white bg-opacity-60 z-10 rounded-lg">
                  <div className="w-8 h-8 md:w-10 md:h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                {/* Category and Subject Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  {/* Category */}
                  <div>
                    <label
                      htmlFor="category"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-700 mb-2"
                    >
                      {texts.category}
                    </label>
                    <select
                      id="category"
                      name="category"
                      value={feedbackData.category}
                      onChange={handleInputChange}
                      className="w-full px-3 py-3 md:py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-base md:text-sm bg-white dark:bg-white text-gray-900"
                      required
                    >
                      <option value="">{texts.selectCategory}</option>
                      {categories.map((cat, index) => (
                        <option key={index} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Subject */}
                  <div>
                    <label
                      htmlFor="subject"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-700 mb-2"
                    >
                      {texts.subject}
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={feedbackData.subject}
                      onChange={handleInputChange}
                      placeholder={texts.subjectPlaceholder}
                      className="w-full px-3 py-3 md:py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-base md:text-sm bg-white dark:bg-white text-gray-900"
                      required
                    />
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-700 mb-2"
                  >
                    {texts.message}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={feedbackData.message}
                    onChange={handleInputChange}
                    placeholder={texts.messagePlaceholder}
                    rows={5}
                    className="w-full px-3 py-3 md:py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical text-base md:text-sm bg-white dark:bg-white text-gray-900"
                    required
                  />
                </div>

                {/* Screenshots */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-700 mb-2">
                    {texts.screenshots}
                  </label>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-600 mb-3">
                    {texts.screenshotsDesc}
                  </p>

                  {/* File Upload Button */}
                  <div className="mb-4">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      multiple
                      accept="image/*"
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={feedbackData.screenshots.length >= 3}
                      className="w-full md:w-auto px-4 py-3 md:py-2 bg-gray-100 dark:bg-gray-100 text-gray-700 dark:text-gray-700 rounded-md border border-gray-300 hover:bg-gray-200 dark:hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
                    >
                      📎 {texts.addScreenshots}
                    </button>
                    <span className="block md:inline ml-0 md:ml-2 mt-1 md:mt-0 text-xs text-gray-500 dark:text-gray-500">
                      ({feedbackData.screenshots.length}
                      /3)
                    </span>
                  </div>

                  {/* Screenshot Previews */}
                  {feedbackData.screenshots.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                      {feedbackData.screenshots.map((screenshot) => (
                        <div key={screenshot.id} className="relative group">
                          <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src={screenshot.preview}
                              alt={screenshot.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeScreenshot(screenshot.id)}
                            className="absolute -top-2 -right-2 w-7 h-7 md:w-6 md:h-6 bg-red-500 text-white rounded-full text-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors opacity-100 md:opacity-0 md:group-hover:opacity-100"
                          >
                            ×
                          </button>
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500 truncate">
                            {screenshot.name}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Submit Buttons */}
                <div className="flex flex-col md:flex-row justify-end space-y-3 md:space-y-0 md:space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="w-full md:w-auto px-6 py-3 md:py-2 border border-gray-300 text-gray-700 dark:text-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-colors text-base md:text-sm"
                  >
                    {texts.cancel}
                  </button>
                  <button
                    type="submit"
                    disabled={feedbackLoading}
                    className="w-full md:w-auto px-6 py-3 md:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-base md:text-sm"
                  >
                    {feedbackLoading ? (
                      <span className="flex items-center justify-center">
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        {language === "en" ? "Submitting..." : "提交中..."}
                      </span>
                    ) : (
                      texts.submit
                    )}
                  </button>
                </div>
              </form>
            </div>
            <div className="text-center font-medium text-gray-600 dark:text-slate-300 px-4 text-sm md:text-base">
              {language === "en" ? (
                <>
                  If the feedback system is not working, please email us at{" "}
                  <a
                    href="mailto:eugeniusedu@oomii.com"
                    className="underline text-blue-500 dark:text-blue-400 break-all"
                  >
                    eugeniusedu@oomii.com
                  </a>
                </>
              ) : (
                <>
                  如果反饋系統無法正常工作，請通過電子郵件與我們聯繫{" "}
                  <a
                    href="mailto:eugeniusedu@oomii.com"
                    className="underline text-blue-500 dark:text-blue-400 break-all"
                  >
                    eugeniusedu@oomii.com
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
